import { CSSProperties, useRef } from "react";
import { components, mergeStyles, SingleValueProps } from "react-select";
import { GroupBase, OptionProps, GroupProps, StylesConfig, PropsValue } from "react-select";

import useSystemStore from "@store/useSystemStore";
import { cn } from "@utils/tailwind.util";
import { APP_COLORS } from "@styles/colors";
import { AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT } from "@config/constants";
import { CompanyStageStatus } from "@enums/companies.enum";
import { DealStageStatus } from "@enums/common.enum";
import { MeetingType } from "@enums/meetings.enum";
import { PipelineStageBadge } from "./PipelineStageBadge";
import { Select, SelectProps } from "@ui/Select";
import { useMaxMenuHeight } from "@components/AddNewEntity/hooks/useMaxMenuHeight";
import { useNoStageLabel } from "@hooks/useNoStageLabel";

export function getCustomSelectStyles<OptionType>(
  useButtonStyles: boolean,
  value: PropsValue<IPipelineStageSelectOption> | undefined | null,
  maxMenuHeight: number
) {
  const { isDarkMode } = useSystemStore();
  const defaultBackgroundColor = isDarkMode ? APP_COLORS.gray[850] : "transparent";

  const v = Array.isArray(value) ? value[0] : value;
  const color = v && "color" in v && v?.color;
  return {
    group: (provided) => ({
      ...provided,
      padding: "0",
    }),
    groupHeading: (provided) => ({
      ...provided,
      paddingTop: 8,
      fontSize: 13,
      color: APP_COLORS.content.secondary,
    }),
    control: (provided) => ({
      ...provided,
      whiteSpace: "pre",
      backgroundColor: useButtonStyles && color ? `${color + "14"}` : defaultBackgroundColor,
      ...(useButtonStyles && {
        border: "none",
        boxShadow: "none",
        borderRadius: "8px",
        "&:hover": {
          border: "none",
          boxShadow: "none",
        },
      }),
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "pre",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: maxMenuHeight,
    }),
  } as StylesConfig<OptionType>;
}

export interface ButtonSelectorOption {
  id?: string;
  label: string;
  value: string;
  color?: string;
  status?: DealStageStatus;
  type?: MeetingType | undefined;
  disabled?: boolean;
}

export interface IPipelineStageSelectOption extends Omit<ButtonSelectorOption, "status"> {
  value: string;
  label: string;
  status: CompanyStageStatus | DealStageStatus | null;
  is_default?: boolean;
}

interface IPipelineStageSelectProps extends SelectProps<IPipelineStageSelectOption, false> {
  useButtonStyles?: boolean;
  showGroupHeaders?: boolean;
  menuPortalTarget?: HTMLElement | null;
}

export function PipelineStageSelect({
  useButtonStyles = false,
  showGroupHeaders = true,
  menuPortalTarget,
  ...rest
}: IPipelineStageSelectProps) {
  const { isDarkMode } = useSystemStore();
  const noStageLabel = useNoStageLabel();
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const maxMenuHeight = useMaxMenuHeight(dropdownButtonRef);

  let customStyles = getCustomSelectStyles(useButtonStyles, rest.value, maxMenuHeight);

  if (!showGroupHeaders) {
    customStyles = {
      ...customStyles,
      groupHeading: (provided) => ({ ...provided, display: "none" }),
    };
  }

  const CustomOption = (
    props: JSX.IntrinsicAttributes &
      OptionProps<IPipelineStageSelectOption, false, GroupBase<IPipelineStageSelectOption>>
  ) => {
    return (
      <components.Option {...props}>
        <div className="custom-option flex gap-2">
          {props.data.id !== "select-all" ? (
            <div
              className={cn("inline-flex h-5 w-5 flex-shrink-0 items-center justify-center", {
                "translate-y-0.5": props.data.id,
              })}
            >
              <PipelineStageBadge stage={props.data} />
            </div>
          ) : null}
          <p className="overflow-hidden text-ellipsis whitespace-pre text-sm font-medium">
            {props.data.label}
          </p>
        </div>
      </components.Option>
    );
  };

  const SingleValue = (
    props: SingleValueProps<
      IPipelineStageSelectOption,
      false,
      GroupBase<IPipelineStageSelectOption>
    >
  ) => {
    const { data } = props;
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center gap-1">
          <span className="inline-flex h-5 w-5 items-center justify-center">
            <PipelineStageBadge stage={data} />
          </span>
          <span className="inline-block overflow-hidden text-ellipsis whitespace-nowrap">
            {data.label}
          </span>
        </div>
      </components.SingleValue>
    );
  };

  const Group = (props: GroupProps<IPipelineStageSelectOption, false>) => {
    const {
      getStyles,
      children,
      label,
      Heading,
      selectProps,
      theme,
      getClassNames,
      cx,
      headingProps,
    } = props;
    const lastOption = rest?.options?.[rest.options.length - 1];
    const isLastOption = props.label === lastOption?.label;

    let disablePortfolioStages = false;
    if (label === AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT.PORTFOLIO_PIPELINE) {
      const portfolioStagesGroup = rest?.options?.find(
        (i) => i.label === AVAILABLE_GROUP_LABELS_FOR_PIPELINE_STAGE_SELECT.PORTFOLIO_PIPELINE
      );

      if (portfolioStagesGroup) {
        disablePortfolioStages =
          ("options" in portfolioStagesGroup &&
            portfolioStagesGroup?.options?.every((i: IPipelineStageSelectOption) => i.disabled)) ||
          false;
      }
    }

    return (
      <div
        aria-label={label as string}
        style={{
          ...(getStyles("group", props) as CSSProperties),
          borderColor: isDarkMode ? APP_COLORS.gray["700"] : APP_COLORS.gray["200"],
          borderBottomWidth: isLastOption ? 0 : 1,
        }}
      >
        {label !== noStageLabel && (
          <Heading
            selectProps={selectProps}
            theme={theme}
            getStyles={getStyles}
            getClassNames={getClassNames}
            cx={cx}
            {...headingProps}
          >
            {label}
            {disablePortfolioStages && (
              <div className="-mx-2 mt-1 rounded-lg border border-stroke-primary dark:border-gray-600 bg-background-secondary dark:bg-gray-800 p-2 font-normal normal-case text-content-secondary dark:text-invertedPrimary80">
                Add company to deal to enable portfolio stages
              </div>
            )}
          </Heading>
        )}
        <div className={cn({ "opacity-40": disablePortfolioStages })}>{children}</div>
      </div>
    );
  };

  const restStyles = {
    className: useButtonStyles
      ? cn(
          "!rounded-md !border-transparent bg-[#F3F4F6] shadow-[0_-1.6px_0_0_#0000000A_inset]",
          "[&_.react-dropdown-select-content]:max-w-full",
          "focus:bg-gray-200",
          "active:bg-[#D1D3DB]",
          "hover:bg-gray-200"
        )
      : "",
  };

  return (
    <div ref={dropdownButtonRef}>
      <Select<IPipelineStageSelectOption, false>
        {...rest}
        isMulti={false}
        isSearchable={false}
        className={cn(rest.className, restStyles.className)}
        styles={rest.styles ? mergeStyles(customStyles, rest.styles) : customStyles}
        components={{ Option: CustomOption, SingleValue, Group, ...(rest.components || {}) }}
        menuPortalTarget={menuPortalTarget}
      />
    </div>
  );
}
